import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ModalController } from '@ionic/angular';
import { SelectOption } from 'src/app/core/component/form-inputs/select-input/select-input.component';
import { Utilities } from 'src/app/core/helper/util';
import { MediaDisplay, Page, PageType } from 'src/app/data/schema/cbs/page';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Log } from 'src/app/core/log';

// added config so we don't have to add more columns to page. 
// should have made media block a regular block :\
export class MediaConfig {
  imgWidth: number = 50;
  minHeight: string = '40vh';
  vertPos: string = 'flex-start'; 
}

@Component({
  selector: 'app-entity-media',
  templateUrl: './entity-media.component.html',
  styleUrls: ['./entity-media.component.scss'],
})
export class EntityMediaComponent implements OnInit, OnDestroy {

  @Input() entity: Page;
  @Input() showMediaDisplay: boolean = true;
  @Input() contentTabName: string = 'Content';
 
  get MediaDisplay() { return MediaDisplay;}
  get PageType() { return PageType;}
  
  mediaOptions: SelectOption[] = [
    {value:false, displayValue: 'Images'},
    {value:true, displayValue: 'Video'},
  ]

  // sanitized video url for preview
  vidUrlSani: any; 

  // parsed out array of image URLs
  images: string[] = [];

  resizedFilePath: string; // path to local, in-memory file
  resizedFileBase64: string;
  resizedFileSize: string;

  sortMode: boolean = false;

  startW: number = 150;
  startH: number = 250;
  resizeInterval: any;
  previewBannerHeight: string;
  previewBannerMaxHeight: string;
  previewDiv: HTMLDivElement;
  previewW: number = this.startW;
  previewMinW: number = this.startW;
  previewMaxW: number = 400;
  growing: boolean = true;
  showing: boolean = false;

  calcPreviewHeights() {
    Log.debug('calc heights')
    if (this.previewDiv == null ) {
      this.previewDiv = document.getElementById('banner-preview') as HTMLDivElement;
    }
    if (this.previewDiv == null ) {return;}
    const w = this.previewDiv.offsetWidth;
    switch(this.entity.bannerRatio){
      case 'calc(100vw)':this.previewBannerHeight = w + 'px';break;
      case 'calc(100vw / 4 * 3)':this.previewBannerHeight = (w * (3/4))+'px';break;
      case 'calc(100vw / 16 * 9)':this.previewBannerHeight = (w * (9/16))+'px';break;
      case 'calc(100vw / 2 * 1)':this.previewBannerHeight = (w / (2))+'px';break;
      case 'calc(100vw / 3 * 1)':this.previewBannerHeight = (w / (3))+'px';break;
    }
    switch(this.entity.bannerMaxH){
      case '10vh':this.previewBannerMaxHeight = (this.startH * 0.1)+'px' ;break;
      case '20vh':this.previewBannerMaxHeight = (this.startH * 0.2)+'px' ;break;
      case '30vh':this.previewBannerMaxHeight = (this.startH * 0.3)+'px' ;break;
      case '40vh':this.previewBannerMaxHeight = (this.startH * 0.4)+'px' ;break;
      case '50vh':this.previewBannerMaxHeight = (this.startH * 0.5)+'px' ;break;
      case '60vh':this.previewBannerMaxHeight = (this.startH * 0.6)+'px' ;break;
      case '70vh':this.previewBannerMaxHeight = (this.startH * 0.7)+'px' ;break;
      case '80vh':this.previewBannerMaxHeight = (this.startH * 0.8)+'px' ;break;
      case '90vh':this.previewBannerMaxHeight = (this.startH * 0.9)+'px' ;break;
      case '100vh':this.previewBannerMaxHeight = this.startH +'px'  ;break;
    }
  }
    
  
  constructor(public sanitizer: DomSanitizer, private modalCtrl: ModalController) { 
  }

  ngOnInit() {
    if ( !Utilities.stringEmpty(this.entity.videoUrl)) {
      this.vidUrlSani = this.sanitizer.bypassSecurityTrustResourceUrl(this.entity.videoUrl);
    }
    this.parseImgUrls();

    if ( this.entity.config != null ) {
      this.entity.cfgObj = JSON.parse(this.entity.config);
    } else {
      this.entity.cfgObj = new MediaConfig();
    }

    setTimeout(() => {
      this.calcPreviewHeights();
    }, 100);
  }

  ngOnDestroy(){
    this.showing = false
  }

  async showScaling() {
    if ( this.showing ) { return;}
    this.showing = true;
    if (this.previewDiv == null ) {
      this.previewDiv = document.getElementById('banner-preview') as HTMLDivElement;
    }
    if ( this.previewDiv != null ) {
      while ( this.showing == true ) {
        if ( this.growing ) { 
          this.previewDiv.style.width = (this.previewDiv.offsetWidth + 5) + 'px';
          this.previewW += 5;
          if ( this.previewW > this.previewMaxW) { this.growing = false}
        }
        else  { 
          this.previewDiv.style.width = (this.previewDiv.offsetWidth - 5) + 'px';
          this.previewW -= 5;
          if ( this.previewW < this.previewMinW) { 
            this.growing = true;
            this.showing = false;
          }
        }
        this.calcPreviewHeights();
        await Utilities.wait(50);
      }
    } else{
      this.showing = false;
    }
  }

  videoUrlEntered() {
    if ( !Utilities.stringEmpty(this.entity.videoUrl)) {
      this.vidUrlSani = this.sanitizer.bypassSecurityTrustResourceUrl(this.entity.videoUrl);
    } else {
      this.vidUrlSani = undefined;
    }
  }

  remove(idx: number){
    this.images.splice(idx, 1);
    this.entity.imagePaths = this.images.join('|');
    this.parseImgUrls();
  }

  onDrop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.images,
      event.previousIndex,
      event.currentIndex
    );
    if ( event.previousIndex != event.currentIndex) {
      this.updateSortOrder();
    }
  }

  async updateSortOrder() {
    Log.debug('image array now:' + this.images)
    this.entity.imagePaths = this.images.join('|');
  }

  parseImgUrls(){
    this.images = Utilities.stringEmpty(this.entity.imagePaths) ? [] : this.entity.imagePaths.split('|');
  }

  updConfig() {
    // This is now handled in PageBase presave() function
    //this.entity.config = JSON.stringify(this.cfgObj);
  }

  pinFormatter(value: number) {
    return `${value}%`;
  }

}
